import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useBrainBanks, BrainBankOption } from "../hooks/useBrainBanks";
import { useSession } from "./SessionContext";

interface KnowledgeBaseContextProps {
  knowledgeBasesList: BrainBankOption[];
  selectedKnowledgeBase: BrainBankOption | null;
  setSelectedKnowledgeBase: (knowledgeBase: BrainBankOption | null) => void;
  restoreSessionKnowledgeBase: (
    sessionId: string,
    knowledgeBaseId?: string | null
  ) => Promise<void>;
  fetchKnowledgeBasesList: () => Promise<BrainBankOption[] | undefined>;
  /**
   * The BrainBank the open conversation is pinned to on the server, whether or
   * not it is in this user's list (a project chat another member started can
   * be pinned to one they cannot see). Null for a conversation without one.
   */
  pinnedKnowledgeBaseId: string | null;
  /** Record that the first message with a BrainBank has pinned this chat. */
  markKnowledgeBasePinned: (knowledgeBaseId: string) => void;
  loading: boolean;
  error: string | null;
}

const KnowledgeBaseContext = createContext<KnowledgeBaseContextProps | undefined>(undefined);

export const useKnowledgeBase = (): KnowledgeBaseContextProps => {
  const context = useContext(KnowledgeBaseContext);
  if (!context) {
    throw new Error("useBrainBank must be used within a KnowledgeBaseProvider");
  }
  return context;
};

interface KnowledgeBaseProviderProps {
  children: React.ReactNode;
}

/**
 * The BrainBank picked for the current chat.
 *
 * The list itself comes from useBrainBanks, the same hook the meetings export
 * dialog uses, so the chat picker cannot drift from the access rules the rest of
 * the app shows. It is loaded lazily -- this provider wraps the whole app, and
 * the list is only needed once a picker is actually mounted, so KnowledgeBaseInput
 * asks for it on mount the way HatInput asks for the VA list.
 *
 * The selection is deliberately not persisted: it is scoped to the conversation
 * in front of the user, exactly like selectedHat, and nothing is ever written
 * back to a VA whose own BrainBanks it stands in for.
 *
 * A session row records the BrainBank its first message used (knowledge_base_id),
 * so opening a conversation restores it through restoreSessionKnowledgeBase.
 * Moving to a conversation any other way clears it, because the honest state for
 * a conversation we know nothing about is none, rather than whichever BrainBank
 * the last chat left selected.
 */
export const KnowledgeBaseProvider: React.FC<KnowledgeBaseProviderProps> = ({ children }) => {
  const { brainBanks, loading, error, refresh } = useBrainBanks(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBaseState] =
    useState<BrainBankOption | null>(null);
  const [pinnedKnowledgeBaseId, setPinnedKnowledgeBaseId] = useState<string | null>(null);

  // The server keeps the first BrainBank a conversation used; so does this.
  const markKnowledgeBasePinned = useCallback((knowledgeBaseId: string) => {
    setPinnedKnowledgeBaseId((current) => current || knowledgeBaseId);
  }, []);

  const { sessionId } = useSession();
  const previousSessionIdRef = useRef(sessionId);
  // The conversation the last restore was for, so the switch that restore rides
  // along with does not immediately clear what it just restored.
  const restoredSessionIdRef = useRef<string | null>(null);
  // Bumped by every selection change. A restore still waiting on the list only
  // applies its answer if nothing has changed the selection since it started --
  // the user opening another conversation, or picking a BrainBank themselves.
  const selectionVersionRef = useRef(0);

  const setSelectedKnowledgeBase = useCallback(
    (knowledgeBase: BrainBankOption | null) => {
      selectionVersionRef.current += 1;
      setSelectedKnowledgeBaseState(knowledgeBase);
    },
    []
  );

  // Show the BrainBank the conversation was started with, not whatever is
  // selected right now. The list may still be loading when the conversation is
  // opened (straight after a page refresh it usually is), so a BrainBank not in
  // it yet is looked up again once it has loaded, instead of being given up on.
  const restoreSessionKnowledgeBase = useCallback(
    async (targetSessionId: string, knowledgeBaseId?: string | null) => {
      restoredSessionIdRef.current = targetSessionId;
      const version = ++selectionVersionRef.current;
      setPinnedKnowledgeBaseId(
        typeof knowledgeBaseId === "string" && knowledgeBaseId ? knowledgeBaseId : null
      );

      if (typeof knowledgeBaseId !== "string" || !knowledgeBaseId) {
        setSelectedKnowledgeBaseState(null);
        return;
      }

      const known = brainBanks.find((knowledgeBase) => knowledgeBase.id === knowledgeBaseId);
      if (known) {
        setSelectedKnowledgeBaseState(known);
        return;
      }

      // Nothing from the previous conversation is left showing while it loads.
      setSelectedKnowledgeBaseState(null);
      const loaded = await refresh();
      if (version !== selectionVersionRef.current) return;
      setSelectedKnowledgeBaseState(
        loaded?.find((knowledgeBase) => knowledgeBase.id === knowledgeBaseId) ?? null
      );
    },
    [brainBanks, refresh]
  );

  useEffect(() => {
    const previousSessionId = previousSessionIdRef.current;
    previousSessionIdRef.current = sessionId;

    // Only a move between conversations clears it. The first send of a new chat
    // also changes sessionId -- from "" to the row it just created -- and the
    // BrainBank picked before that send belongs to this conversation. A move
    // made by opening a conversation has already restored that conversation's
    // own BrainBank, which must survive it.
    if (restoredSessionIdRef.current === sessionId) {
      restoredSessionIdRef.current = null;
      return;
    }
    if (previousSessionId && previousSessionId !== sessionId) {
      setSelectedKnowledgeBase(null);
      setPinnedKnowledgeBaseId(null);
    }
  }, [sessionId, setSelectedKnowledgeBase]);

  return (
    <KnowledgeBaseContext.Provider
      value={{
        knowledgeBasesList: brainBanks,
        selectedKnowledgeBase,
        setSelectedKnowledgeBase,
        restoreSessionKnowledgeBase,
        fetchKnowledgeBasesList: refresh,
        pinnedKnowledgeBaseId,
        markKnowledgeBasePinned,
        loading,
        error,
      }}
    >
      {children}
    </KnowledgeBaseContext.Provider>
  );
};

export default KnowledgeBaseProvider;
